import { defaultAllSelection } from "services/constants/selections";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AccessTimeOutlined,
  CalendarMonthOutlined,
  CheckCircleOutline,
  EventAvailableOutlined,
  FactCheckOutlined,
  FlightTakeoffOutlined,
  HourglassBottomOutlined,
  InfoOutlined,
  LocationOnOutlined,
  MoreTimeOutlined,
  PersonOffOutlined,
} from "@mui/icons-material";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { Loader } from "components/Loader";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { FilterProvider } from "pages/FilterContext";
import { scrollTop } from "services/constants";
import { useFetch } from "services/hooks/useFetch";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";
import {
  designFontFamily,
  designTheme,
  designWeights,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";

const keys = [
  "date",
  "shift",
  "type",
  "status",
  "status_label",
  "check_in_time",
  "check_out_time",
  "Leave_credit_hours",
  "break_hours",
  "working_hours",
  "over_time",
  "total_permission_taken",
  "Late_days",
];

const ATTENDANCE_HISTORY_DEFAULT_VIEW_ID = "__default_my_attendance_history_view__";
const ATTENDANCE_HISTORY_DEFAULT_HEADER_KEYS = [
  "date",
  "shift",
  "check_in_time_formatted",
  "check_out_time_formatted",
  "break_hours",
  "over_time",
  "working_hours",
  "status_label",
];

const attendanceHistoryHeaders = [
  {
    name: "Date",
    key: "date",
    type: "date",
    icon: <CalendarMonthOutlined fontSize="small" />,
  },
  {
    name: "Shift",
    key: "shift",
    type: "autocomplete",
    icon: <FactCheckOutlined fontSize="small" />,
  },
  {
    name: "Check-In",
    key: "check_in_time_formatted",
    type: "time",
    icon: <AccessTimeOutlined fontSize="small" />,
  },
  {
    name: "Check-Out",
    key: "check_out_time_formatted",
    type: "time",
    icon: <AccessTimeOutlined fontSize="small" />,
  },
  {
    name: "Break",
    key: "break_hours",
    type: "time",
    icon: <HourglassBottomOutlined fontSize="small" />,
  },
  {
    name: "Overtime",
    key: "over_time",
    type: "time",
    icon: <MoreTimeOutlined fontSize="small" />,
  },
  {
    name: "Working Hours",
    key: "working_hours",
    type: "time",
    icon: <AccessTimeOutlined fontSize="small" />,
  },
  {
    name: "Status",
    key: "status_label",
    type: "autocomplete",
    icon: <CheckCircleOutline fontSize="small" />,
  },
];

const resolveWorkingHoursSubModuleId = (userScreenAccess) => {
  const modules = userScreenAccess?.module_details || [];

  for (const module of modules) {
    const match = module?.submodules?.find((submodule) =>
      ["Working Hours", "My Attendance"].includes(submodule?.sub_module_name),
    );
    if (match?.sub_module_id) return match.sub_module_id;
  }

  return undefined;
};

const cardSx = {
  border: "1px solid #E4EAF2",
  borderRadius: "8px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0 8px 28px rgba(15, 23, 42, 0.04)",
};

const statusFallback = {
  P: "Present",
  A: "Absent",
  W: "Week Off",
  H: "Holiday",
  M: "Missing",
  L: "Leave",
  LA: "Leave / Absent",
  AL: "Absent / Leave",
  LP: "Leave / Present",
  PL: "Present / Leave",
  HL: "Holiday / Leave",
};

const statusColors = {
  Present: { color: "#0F9F6E", bg: "#E2F8EA" },
  Working: { color: "#0F9F6E", bg: "#E2F8EA" },
  Absent: { color: "#EF4444", bg: "#FFE5E5" },
  Leave: { color: "#6D4AFF", bg: "#EFEAFF" },
  "Week Off": { color: "#2563EB", bg: "#E7F0FF" },
  Holiday: { color: "#F59E0B", bg: "#FFF4D8" },
  Missing: { color: "#F97316", bg: "#FFEEE4" },
};

const safeValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "" || value === "null") {
    return fallback;
  }
  return value;
};

const cleanAttendanceValue = (value) => safeValue(value, null);

// checkinfilter's break_details entries use break_out_formatted/break_in_formatted
// while individual_details_working_hours entries use plain break_out/break_in.
// The API also doesn't guarantee chronological order (it has been observed
// returning the most recently taken break first), so this normalizes the
// field names AND sorts every break entry oldest-to-newest by its break-out
// time, combined with its own break_start_date when available.
const normalizeBreakDetails = (breakDetails) => {
  if (!Array.isArray(breakDetails)) return [];

  const breakOutMoment = (brk) => {
    const time = brk?.break_out || brk?.break_out_formatted;
    if (!time) return null;
    const datePrefix = brk?.break_start_date ? `${brk.break_start_date} ` : "";
    const parsed = moment(`${datePrefix}${time}`, [
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD HH:mm:ss",
      "HH:mm",
      "HH:mm:ss",
    ]);
    return parsed.isValid() ? parsed : null;
  };

  return breakDetails
    .map((brk) => ({
      ...brk,
      break_out: brk?.break_out || brk?.break_out_formatted,
      break_in: brk?.break_in || brk?.break_in_formatted,
    }))
    .sort((a, b) => {
      const momentA = breakOutMoment(a);
      const momentB = breakOutMoment(b);
      if (!momentA || !momentB) return 0;
      return momentA.valueOf() - momentB.valueOf();
    });
};

const hasOpenBreak = (row) => {
  const breaks = normalizeBreakDetails(row?.break_details);
  const latestBreak = breaks[breaks.length - 1] || {};
  return Boolean(latestBreak?.break_out && !latestBreak?.break_in);
};

const normalizeTodayAttendanceRow = (row) => {
  if (!row) return null;

  const currentTag = cleanAttendanceValue(row?.current_location_tag);
  const checkedIn = Boolean(cleanAttendanceValue(row?.check_in_time_formatted || row?.check_in_time));
  const checkedOut = Boolean(cleanAttendanceValue(row?.check_out_time_formatted || row?.check_out_time));
  const isOnBreak = hasOpenBreak(row) || (currentTag || "").toLowerCase().includes("break");

  // attendance_status is checkinfilter's own purpose-built status field (e.g.
  // "Out"), so it's the source of truth whenever the backend sets it. The
  // live isOnBreak/checkedIn heuristics below only step in as a fallback for
  // when the backend hasn't provided one at all - they used to run
  // unconditionally and silently overwrite a valid attendance_status value.
  const backendStatus = cleanAttendanceValue(row?.attendance_status);
  let displayStatus =
    backendStatus ||
    cleanAttendanceValue(row?.card_display_status) ||
    cleanAttendanceValue(row?.status_label) ||
    cleanAttendanceValue(row?.type);
  let displayColor = row?.card_display_status_color || row?.status_color || row?.color;

  if (!backendStatus) {
    if (isOnBreak) {
      displayStatus = currentTag || "On Break";
      displayColor = "#F59E0B";
    } else if (checkedIn && !checkedOut) {
      displayStatus = currentTag || "Checked In";
      displayColor = "#0F9F6E";
    }
  }

  return {
    ...row,
    date: row?.date || moment().format("DD-MM-YYYY"),
    shift: row?.shift || row?.shift_name || row?.attendance_policy || "-",
    check_in_time_formatted: row?.check_in_time_formatted || row?.check_in_time,
    check_out_time_formatted: row?.check_out_time_formatted || row?.check_out_time,
    working_hours: row?.working_hours || row?.total_work_time || "00:00",
    break_hours: row?.break_hours || row?.total_break_time || "00:00",
    over_time: row?.over_time || row?.overtime || row?.total_over_time || "00:00",
    location_name: row?.location_name || row?.checkin_location_name || row?.location,
    display_status_label: displayStatus || getStatusLabel(row?.status),
    color: displayColor,
    break_details: normalizeBreakDetails(row?.break_details),
  };
};

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const firstNumericValue = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== "") {
      return numberValue(value);
    }
  }
  return null;
};

// Sums "HH:mm" duration strings (e.g. row-level over_time values) into one
// "HH:mm" total, ignoring anything that doesn't parse.
const sumHhMm = (values) => {
  const totalMinutes = (values || []).reduce((sum, value) => {
    const parsed = moment(value, ["HH:mm", "H:mm", "HH:mm:ss"], true);
    if (!parsed.isValid()) return sum;
    return sum + parsed.hours() * 60 + parsed.minutes();
  }, 0);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const rowMatchesStatus = (row, matcher) => {
  const values = [
    row?.status,
    row?.status_label,
    row?.type,
    row?.card_display_status,
    row?.attendance_status,
  ];
  return values.some((value) => matcher((value || "").toString().toLowerCase()));
};

const formatDate = (value, output = "ddd, DD MMM YYYY") => {
  if (!value) return "-";
  const parsed = moment(value, ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format(output) : value;
};

const getStatusLabel = (status, statusLabelMap = {}) =>
  statusLabelMap?.[status] || statusFallback?.[status] || status || "-";

const getStatusPalette = (label, fallbackColor) =>
  statusColors[label] || {
    color: fallbackColor || "#64748B",
    bg: fallbackColor ? `${fallbackColor}22` : "#F1F5F9",
  };

const getDisplayTime = (value) => safeValue(value, "--:--");

const getLocation = (row, type = "checkin") => {
  if (type === "checkout") {
    return safeValue(row?.checkout_location_name || row?.location_name || row?.location, "-");
  }
  return safeValue(row?.checkin_location_name || row?.location_name || row?.location, "-");
};

const MetricTile = ({ icon, label, value, unit, color, bg, compact = false }) => (
  <Mui.Box
    sx={{
      ...cardSx,
      p: compact ? 1.1 : 1.5,
      minHeight: compact ? 66 : 84,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    <Mui.Stack direction="row" alignItems="center" spacing={compact ? 0.75 : 1}>
      <Mui.Box
        sx={{
          width: compact ? 30 : 36,
          height: compact ? 30 : 36,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
          backgroundColor: bg,
          flexShrink: 0,
          "& svg": {
            fontSize: compact ? 19 : 24,
          },
        }}
      >
        {icon}
      </Mui.Box>
      <Mui.Typography sx={{ fontSize: compact ? 12 : 13, fontWeight: designWeights.semibold, color: "#17203B" }}>
        {label}
      </Mui.Typography>
    </Mui.Stack>
    <Mui.Box sx={{ pl: compact ? 4.6 : 5.25 }}>
      <Mui.Typography sx={{ fontSize: compact ? 20 : 24, lineHeight: 1, fontWeight: designWeights.heading, color: "#111A35" }}>
        {value}
      </Mui.Typography>
      <Mui.Typography sx={{ mt: 0.25, fontSize: 11, color: "#52607A", fontWeight: designWeights.regular }}>
        {unit}
      </Mui.Typography>
    </Mui.Box>
  </Mui.Box>
);

const StatusChip = ({ row, statusLabelMap }) => {
  const label =
    row?.display_status_label ||
    row?.card_display_status ||
    row?.status_label ||
    getStatusLabel(row?.status, statusLabelMap);
  const palette = getStatusPalette(label, row?.color);

  return (
    <Mui.Chip
      label={label}
      size="small"
      sx={{
        height: 24,
        borderRadius: "6px",
        backgroundColor: palette.bg,
        color: palette.color,
        fontSize: 12,
        fontWeight: designWeights.semibold,
      }}
    />
  );
};

const DetailLine = ({ label, value, chip }) => (
  <Mui.Grid container spacing={1} sx={{ py: 0.45 }}>
    <Mui.Grid item xs={5}>
      <Mui.Typography sx={{ fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
        {label}
      </Mui.Typography>
    </Mui.Grid>
    <Mui.Grid item xs={1}>
      <Mui.Typography sx={{ fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
        :
      </Mui.Typography>
    </Mui.Grid>
    <Mui.Grid item xs={6}>
      {chip || (
        <Mui.Typography sx={{ fontSize: 12, color: "#17203B", fontWeight: designWeights.medium }}>
          {safeValue(value)}
        </Mui.Typography>
      )}
    </Mui.Grid>
  </Mui.Grid>
);

const TimelineItem = ({ item, isLast }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ position: "relative", pb: isLast ? 0 : 1.25 }}>
    <Mui.Box sx={{ position: "relative", width: 12, display: "flex", justifyContent: "center", alignItems: "center", alignSelf: "stretch" }}>
      <Mui.Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: item.color,
          zIndex: 1,
        }}
      />
      {!isLast ? (
        <Mui.Box
          sx={{
            position: "absolute",
            top: 14,
            bottom: -11,
            width: 2,
            backgroundColor: "#D8E6F3",
          }}
        />
      ) : null}
    </Mui.Box>
    <Mui.Box sx={{ width: 74, flexShrink: 0 }}>
      <Mui.Typography sx={{ fontSize: 12, color: "#33415F", fontWeight: designWeights.medium }}>
        {item.time}
      </Mui.Typography>
    </Mui.Box>
    <Mui.Box sx={{ minWidth: 0 }}>
      <Mui.Typography sx={{ fontSize: 12, color: "#111A35", fontWeight: designWeights.semibold }}>
        {item.title}
      </Mui.Typography>
    </Mui.Box>
  </Mui.Stack>
);

const AttendanceDetailsPanel = ({ attendance, open, statusLabelMap, onClose }) => {
  if (!attendance) return null;

  const allBreaks = Array.isArray(attendance?.break_details) ? attendance.break_details : [];

  // break_details is already normalized to plain break_out/break_in fields
  // and sorted oldest-to-newest by normalizeBreakDetails, so every break of
  // the day can be laid out on the timeline in the order it actually happened.
  const breakTimelineItems = allBreaks.flatMap((breakItem) => {
    return [
      breakItem?.break_out
        ? {
            time: breakItem.break_out,
            title: "Break Start",
            color: "#F59E0B",
          }
        : null,
      breakItem?.break_in
        ? {
            time: breakItem.break_in,
            title: "Break End",
            color: "#258BEA",
          }
        : null,
    ].filter(Boolean);
  });

  // Overtime can run either before check-in (early OT) or after check-out
  // (late OT), so it can't just be appended at the end like breaks are - the
  // whole timeline is sorted chronologically below instead of assuming a
  // fixed check-in -> breaks -> check-out order.
  const allOvertime = Array.isArray(attendance?.overtime_details)
    ? attendance.overtime_details
    : [];
  const overtimeTimelineItems = allOvertime.flatMap((otItem) => {
    return [
      otItem?.ot_start_formatted
        ? {
            time: otItem.ot_start_formatted,
            title: "Overtime Start",
            color: "#6D4AFF",
          }
        : null,
      otItem?.ot_end_formatted
        ? {
            time: otItem.ot_end_formatted,
            title: "Overtime End",
            color: "#6D4AFF",
          }
        : null,
    ].filter(Boolean);
  });

  const minutesOfDay = (time) => {
    const parsed = moment(time, ["HH:mm", "HH:mm:ss", "hh:mm A"], true);
    return parsed.isValid() ? parsed.hours() * 60 + parsed.minutes() : null;
  };

  const timeline = [
    attendance?.check_in_time_formatted
      ? {
          time: getDisplayTime(attendance.check_in_time_formatted),
          title: "Check In",
          color: "#0F9F6E",
        }
      : null,
    ...breakTimelineItems,
    ...overtimeTimelineItems,
    attendance?.check_out_time_formatted
      ? {
          time: getDisplayTime(attendance.check_out_time_formatted),
          title: "Check Out",
          color: "#0F9F6E",
        }
      : null,
  ]
    .filter(Boolean)
    .sort((a, b) => {
      const minutesA = minutesOfDay(a.time);
      const minutesB = minutesOfDay(b.time);
      if (minutesA === null || minutesB === null) return 0;
      return minutesA - minutesB;
    });

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Attendance Details"
      PaperProps={{ sx: { width: { xs: "100vw", sm: 620 } } }}
    >
      <Mui.Box sx={{ p: 2.5 }}>
      <Mui.Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <CalendarMonthOutlined sx={{ fontSize: 20, color: "#33415F" }} />
        <Mui.Typography sx={{ fontSize: 13, color: "#17203B", fontWeight: designWeights.semibold }}>
          {formatDate(attendance.date, "DD MMM YYYY (dddd)")}
        </Mui.Typography>
      </Mui.Stack>
      <Mui.Divider sx={{ mb: 1.5 }} />

      <DetailLine label="Shift" value={attendance.shift} />
      <DetailLine label="Check In" value={getDisplayTime(attendance.check_in_time_formatted)} />
      <DetailLine label="Check Out" value={getDisplayTime(attendance.check_out_time_formatted)} />
      <DetailLine label="Break" value={safeValue(attendance.break_hours, "00:00")} />
      <DetailLine label="Worked Hours" value={safeValue(attendance.working_hours, "00:00")} />
      <DetailLine label="Overtime" value={safeValue(attendance.over_time, "00:00")} />
      <DetailLine label="Status" chip={<StatusChip row={attendance} statusLabelMap={statusLabelMap} />} />
      <DetailLine label="Leave Credit Used" value={safeValue(attendance.Leave_credit_hours, "0.00")} />

      <Mui.Divider sx={{ my: 2 }} />
      <Mui.Typography sx={{ fontSize: 14, color: "#111A35", fontWeight: designWeights.heading, mb: 1.5 }}>
        Timeline
      </Mui.Typography>
      {timeline.length ? (
        timeline.map((item, index) => (
          <TimelineItem key={`${item.title}-${index}`} item={item} isLast={index === timeline.length - 1} />
        ))
      ) : (
        <Mui.Alert severity="info" sx={{ py: 0.5 }}>
          No punch timeline available.
        </Mui.Alert>
      )}

      </Mui.Box>
    </GlobalSidePanel>
  );
};

const IndividualWorkingHoursContent = ({ subModuleId }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const history = useHistory();
  const { domain, token, globalSearchValue, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [workingHoursDetails, setWorkingHoursDetails] = useState([]);
  const [handleLoading, setHandleLoading] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [todayAttendanceSnapshot, setTodayAttendanceSnapshot] = useState(null);
  const [userWeeklyReport, setUserWeeklyReport] = useState({});
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  // Default range is This Month through today (future dates are disabled).
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "today" }),
  );
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const [currentViewId, setCurrentViewId] = useState(null);
  const containerRef = useRef(null);
  const didLoadInitialRef = useRef(false);

  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);

  const { result: colorCode } = useFetch(`${domain}attendance/status-colors/`);

  const statusLabelMap = useMemo(() => {
    if (!Array.isArray(colorCode)) return {};
    return colorCode.reduce((acc, item) => {
      acc[item.status] = item.label;
      return acc;
    }, {});
  }, [colorCode]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const selectAttendance = (attendance) => {
    setSelectedAttendance(attendance);
    setDetailsOpen(true);
  };

  const loadWorkingHours = useCallback(async (searchStartDate, searchEndDate) => {
    if (!domain || !token || !userDetails?.id) return;

    setStartDate(searchStartDate);
    setEndDate(searchEndDate);
    setHandleLoading(true);
    try {
      const response = await fetch(
        `${domain}individual_details_working_hours/?user_id=${userDetails.id}&start_date=${searchStartDate}&end_date=${searchEndDate}&company_id=${requestCompanyId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        },
      );
      const res = await response.json();
      if (response.ok) {
        // The backend's own summary counts (userWeeklyReport, e.g.
        // working_days) are correctly scoped to [searchStartDate,
        // searchEndDate], but checkin_details has been observed returning
        // an extra row outside that window (e.g. a single-day "Today" query
        // still including yesterday). Clamp defensively so the table can
        // never show more days than were actually requested.
        const rangeStart = moment(searchStartDate, "YYYY-MM-DD");
        const rangeEnd = moment(searchEndDate, "YYYY-MM-DD");
        const rows = (res.checkin_details || [])
          .filter((row) => {
            const rowDate = moment(row?.date, ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
            if (!rowDate.isValid()) return true;
            return rowDate.isBetween(rangeStart, rangeEnd, "day", "[]");
          })
          .map((row) => ({
            ...row,
            break_details: normalizeBreakDetails(row?.break_details),
          }));
        setUserWeeklyReport(res);
        setMasterDataForSearch(rows);
        setWorkingHoursDetails(rows);
        setSelectedAttendance(null);
        setDetailsOpen(false);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        throw new Error("REQUEST_FAILED");
      }
      setHandleLoading(false);
    } catch (error) {
      console.error("[Index] operation failed:", error);
      setHandleLoading(false);
      showToast("error", "Couldn't load your working hours. Please refresh the page.");
    }
  }, [domain, token, userDetails?.id, requestCompanyId]);

  const loadTodayAttendance = useCallback(async () => {
    if (!domain || !token || !userDetails?.id) return;

    try {
      const response = await fetch(
        `${domain}checkinfilter/?department_id=${userDetails?.department_id || defaultAllSelection}&date=${moment().format("YYYY-MM-DD")}&selected_user_id=${userDetails?.id}&company_id=${requestCompanyId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        },
      );
      const res = await response.json();
      if (response.ok) {
        const mine =
          res?.user_status_details?.find(
            (item) => String(item?.user_id) === String(userDetails?.id),
          ) || res?.user_status_details?.[0];
        setTodayAttendanceSnapshot(normalizeTodayAttendanceRow(mine));
      } else if (response.status === 409) {
        sessionHandling();
      }
    } catch (error) {
      console.error("[WorkingHours] today attendance load failed:", error);
    }
  }, [domain, token, userDetails?.department_id, userDetails?.id, requestCompanyId]);

  const handleStartDate = async (selectedStart, selectedEnd, rangeLabel) => {
    let searchStartDate = selectedStart.split("/").reverse().join("-");
    let searchEndDate = selectedEnd.split("/").reverse().join("-");
    const daysDiff = moment(searchEndDate).diff(moment(searchStartDate), "days") + 1;

    // The rewrites below pad near-30/7-day picks; a calendar month (28-31
    // days) must be sent exactly as chosen, so skip them for month presets.
    const isMonthPreset = rangeLabel === "This Month" || rangeLabel === "Last Month";

    if (!isMonthPreset && daysDiff >= 27 && daysDiff <= 29) {
      searchStartDate = moment(searchEndDate).subtract(29, "days").format("YYYY-MM-DD");
    }

    if (!isMonthPreset && daysDiff === 6) {
      searchStartDate = moment(searchEndDate).subtract(6, "days").format("YYYY-MM-DD");
    }

    await loadWorkingHours(searchStartDate, searchEndDate);
  };

  const handleAttendanceDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    setDateRangeLabel(rangeLabel || "Custom Range");
    handleStartDate(
      moment(new Date(rangeStart)).format("DD/MM/YYYY"),
      moment(new Date(rangeEnd)).format("DD/MM/YYYY"),
      rangeLabel,
    );
  };

  useEffect(() => {
    if (didLoadInitialRef.current || !domain || !token || !userDetails?.id) {
      return;
    }

    didLoadInitialRef.current = true;
    loadWorkingHours(startDate, endDate);
  }, [domain, endDate, loadWorkingHours, startDate, token, userDetails?.id]);

  useEffect(() => {
    loadTodayAttendance();
  }, [loadTodayAttendance]);

  const isSameAsToday = (date) =>
    moment(date, ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true).isSame(moment(), "day");

  const parseAttendanceDate = (date) =>
    moment(date, ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true);

  const mergedAttendanceRows = useMemo(() => {
    const isTodayInRange =
      !endDate || moment(endDate, ["YYYY-MM-DD", moment.ISO_8601]).isSameOrAfter(moment(), "day");

    // The history API's own row for "today" (if any) can lag behind what
    // just happened (cached/batched, or off by a day if the backend stamps
    // it in a different timezone), so once the live checkinfilter snapshot
    // is available it always wins for today's date - the API's row for that
    // same date is dropped instead of kept alongside it, which previously
    // let both a correctly-dated live row AND a stale/misdated API row for
    // the same day show up together in the list.
    const rows =
      todayAttendanceSnapshot && isTodayInRange
        ? [
            todayAttendanceSnapshot,
            ...masterDataForSearch.filter((item) => !isSameAsToday(item?.date)),
          ]
        : masterDataForSearch;

    // Belt-and-braces: collapse any remaining rows that resolve to the same
    // calendar date (e.g. a genuine duplicate from the API) down to one,
    // keeping whichever occurrence came first.
    const seenDates = new Set();
    const deduped = rows.filter((item) => {
      const parsed = parseAttendanceDate(item?.date);
      const key = parsed.isValid() ? parsed.format("YYYY-MM-DD") : item?.date;
      if (seenDates.has(key)) return false;
      seenDates.add(key);
      return true;
    });

    // Always show the most recent date first, regardless of the order the
    // API returns rows in or where today's manually-appended row landed.
    return [...deduped].sort((a, b) => {
      const dateA = parseAttendanceDate(a?.date);
      const dateB = parseAttendanceDate(b?.date);
      if (!dateA.isValid() || !dateB.isValid()) return 0;
      return dateB.valueOf() - dateA.valueOf();
    });
  }, [masterDataForSearch, todayAttendanceSnapshot, endDate]);

  const normalizedAttendanceRows = useMemo(
    () =>
      mergedAttendanceRows.map((item) => ({
        ...item,
        status_label: getStatusLabel(item.status, statusLabelMap),
      })),
    [mergedAttendanceRows, statusLabelMap],
  );

  const globalFilteredAttendanceRows = useMemo(() => {
    const searchValue = (globalSearchValue || "").toString().toLowerCase();
    if (!searchValue) return normalizedAttendanceRows;

    return normalizedAttendanceRows.filter((item) =>
      keys.some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(searchValue),
      ),
    );
  }, [globalSearchValue, normalizedAttendanceRows]);

  const handleFilteredDataChange = useCallback(
    (filteredData = []) => {
      setWorkingHoursDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const todayAttendance = useMemo(() => {
    const isToday = (date) =>
      moment(date, ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true).isSame(moment(), "day");
    return (
      todayAttendanceSnapshot ||
      workingHoursDetails.find((item) => isToday(item.date)) ||
      workingHoursDetails[0] ||
      null
    );
  }, [todayAttendanceSnapshot, workingHoursDetails]);

  const weekOffDays = useMemo(() => {
    const apiCount = firstNumericValue(
      userWeeklyReport?.week_off_days,
      userWeeklyReport?.weekoff_days,
      userWeeklyReport?.weekly_off_days,
      userWeeklyReport?.total_week_off_days,
      userWeeklyReport?.total_weekoff_days,
    );
    if (apiCount !== null) return apiCount;
    return masterDataForSearch.filter((item) =>
      rowMatchesStatus(item, (value) =>
        value === "w" ||
        value.includes("week off") ||
        value.includes("weekoff"),
      ),
    ).length;
  }, [masterDataForSearch, userWeeklyReport]);

  const holidayDays = useMemo(() => {
    const apiCount = firstNumericValue(
      userWeeklyReport?.holiday_days,
      userWeeklyReport?.holidays,
      userWeeklyReport?.total_holiday_days,
      userWeeklyReport?.total_holidays,
      userWeeklyReport?.holiday_count,
      userWeeklyReport?.total_holiday_count,
    );
    if (apiCount !== null) return apiCount;
    return masterDataForSearch.filter((item) =>
      rowMatchesStatus(item, (value) =>
        value === "h" || value.includes("holiday"),
      ),
    ).length;
  }, [masterDataForSearch, userWeeklyReport]);

  const attendanceSummary = [
    {
      key: "worked",
      label: "Worked",
      unit: "Days",
      value: userWeeklyReport.working_days || 0,
      icon: <CalendarMonthOutlined />,
      color: "#0F9F6E",
      bg: "#E8F8F1",
    },
    {
      key: "leave",
      label: "Leave",
      unit: "Days",
      value: userWeeklyReport.personal_leave || 0,
      icon: <FlightTakeoffOutlined />,
      color: "#6D4AFF",
      bg: "#EFEAFF",
    },
    {
      key: "absent",
      label: "Absent",
      unit: Number(userWeeklyReport.absent_days || 0) === 1 ? "Day" : "Days",
      value: userWeeklyReport.absent_days || 0,
      icon: <PersonOffOutlined />,
      color: "#EF4444",
      bg: "#FFE5E5",
    },
    {
      key: "holiday",
      label: "Holiday",
      unit: Number(holidayDays) === 1 ? "Day" : "Days",
      value: holidayDays,
      icon: <EventAvailableOutlined />,
      color: "#F59E0B",
      bg: "#FFF4D8",
    },
    {
      key: "weekoff",
      label: "Week Off",
      unit: "Days",
      value: weekOffDays,
      icon: <HourglassBottomOutlined />,
      color: "#258BEA",
      bg: "#E7F0FF",
    },
  ];

  // The backend's own total_over_time is aggregated for the whole requested
  // range, but that aggregate has been observed lagging behind today's live
  // overtime (today's row here is always the live checkinfilter snapshot,
  // not the backend's own possibly-not-yet-finalized row - see
  // mergedAttendanceRows above). Summing every visible row's own over_time
  // instead guarantees the WORK METRICS tile always matches what the table
  // actually shows, current day included, regardless of that backend lag.
  const computedOvertimeTotal = useMemo(
    () => sumHhMm(mergedAttendanceRows.map((row) => row?.over_time)),
    [mergedAttendanceRows],
  );

  const workMetrics = [
    {
      key: "late",
      label: "Late Arrivals",
      unit: "Times",
      value: userWeeklyReport.total_late_days || 0,
      icon: <AccessTimeOutlined />,
      color: "#F59E0B",
      bg: "#FFF4D8",
    },
    {
      key: "early",
      label: "Early Exits",
      unit: "Times",
      value: userWeeklyReport.total_early_exit_days || 0,
      icon: <PersonOffOutlined />,
      color: "#EF4444",
      bg: "#FFE5E5",
    },
    {
      key: "overtime",
      label: "Overtime",
      unit: "Hours",
      value: computedOvertimeTotal,
      icon: <MoreTimeOutlined />,
      color: "#6D4AFF",
      bg: "#EFEAFF",
    },
    {
      key: "permission",
      label: "Permission",
      unit: "Times",
      value: userWeeklyReport.total_permission_days || 0,
      icon: <FactCheckOutlined />,
      color: "#258BEA",
      bg: "#E7F0FF",
    },
    {
      key: "corrections",
      label: "Corrections",
      unit: "Times",
      value: userWeeklyReport.total_correction_days || 0,
      icon: <CheckCircleOutline />,
      color: "#0F9F6E",
      bg: "#E8F8F1",
    },
  ];

  const totalSummaryDays = attendanceSummary.reduce(
    (total, item) => total + numberValue(item.value),
    0,
  );

  const accountedPercent = totalSummaryDays
    ? Math.round((numberValue(userWeeklyReport.working_days) / totalSummaryDays) * 1000) / 10
    : 0;

  const canPersistAttendancePreferences = Boolean(
    domain && userDetails?.id && subModuleId,
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "my_attendance_history_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: ATTENDANCE_HISTORY_DEFAULT_VIEW_ID,
  });

  const persistedDefaultViewId =
    activeViewId || ATTENDANCE_HISTORY_DEFAULT_VIEW_ID;

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== ATTENDANCE_HISTORY_DEFAULT_VIEW_ID) return prev;
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(() => {
    const defaultKeys = new Set(ATTENDANCE_HISTORY_DEFAULT_HEADER_KEYS);
    return attendanceHistoryHeaders.filter((header) =>
      defaultKeys.has(header.key),
    );
  }, []);

  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find(
        (view) => view.id === ATTENDANCE_HISTORY_DEFAULT_VIEW_ID,
      ) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () =>
      savedViewsForScope.filter(
        (view) => view.id !== ATTENDANCE_HISTORY_DEFAULT_VIEW_ID,
      ),
    [savedViewsForScope],
  );

  const defaultView = useMemo(
    () =>
      (storedDefaultView?.isCustomDefault ? storedDefaultView : null) || {
        id: ATTENDANCE_HISTORY_DEFAULT_VIEW_ID,
        name: "Default View",
        headerConfig: baseDefaultHeaders.map((header) => ({
          key: header.key,
          name: header.name,
        })),
        headerKeys: baseDefaultHeaders.map((header) => header.key),
        isDefaultView: true,
      },
    [baseDefaultHeaders, storedDefaultView],
  );

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === ATTENDANCE_HISTORY_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(
      attendanceHistoryHeaders.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return {
            ...baseHeader,
            name: entry.name?.trim() || baseHeader.name,
          };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = attendanceHistoryHeaders.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(ATTENDANCE_HISTORY_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === ATTENDANCE_HISTORY_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(ATTENDANCE_HISTORY_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(ATTENDANCE_HISTORY_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || ATTENDANCE_HISTORY_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(ATTENDANCE_HISTORY_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={attendanceHistoryHeaders}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={canPersistAttendancePreferences}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === ATTENDANCE_HISTORY_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      canPersistAttendancePreferences,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      persistedDefaultViewId,
      updateView,
    ],
  );

  const reportMetadata = useMemo(
    () => ({
      "User Name": userDetails?.username || userDetails?.user_name || "N/A",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Working Days": userWeeklyReport?.working_days ?? 0,
      "Late Days": userWeeklyReport?.total_late_days ?? 0,
      Leave: userWeeklyReport?.personal_leave ?? 0,
      "Break Hours": userWeeklyReport?.total_break_hours ?? 0,
      "Absent Days": userWeeklyReport?.absent_days ?? 0,
      "Over Time": userWeeklyReport?.total_over_time ?? 0,
      Permission: userWeeklyReport?.total_permission_days ?? 0,
      "Weekly Working Hours": userWeeklyReport?.weekly_working_hours ?? 0,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [endDate, startDate, userDetails, userWeeklyReport],
  );

  const paginatedRows = workingHoursDetails?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const renderAttendanceCell = (user, head) => {
    switch (head.key) {
      case "date":
        return formatDate(user.date);
      case "check_in_time_formatted":
        return (
          <Mui.Typography component="span" sx={{ color: "#0F9F6E", fontSize: 12 }}>
            {getDisplayTime(user.check_in_time_formatted)}
          </Mui.Typography>
        );
      case "check_out_time_formatted":
        return getDisplayTime(user.check_out_time_formatted);
      case "break_hours":
        return safeValue(user.break_hours, "00:00");
      case "over_time":
        return (
          <Mui.Typography component="span" sx={{ color: "#6D4AFF", fontSize: 12 }}>
            {safeValue(user.over_time, "00:00")}
          </Mui.Typography>
        );
      case "working_hours":
        return safeValue(user.working_hours, "00:00");
      case "status_label":
        return <StatusChip row={user} statusLabelMap={statusLabelMap} />;
      default:
        return safeValue(user[head.key]);
    }
  };

  const AttendanceMobileCard = ({ row }) => {
    const statusHead = visibleHeaders.find((head) => head.key === "status_label");
    const detailHeads = visibleHeaders.filter(
      (head) => !["date", "status_label"].includes(head.key),
    );

    return (
      <Mui.Box
        onClick={() => selectAttendance(row)}
        sx={{
          p: 1.5,
          border: "1px solid #E4EAF2",
          borderRadius: "8px",
          backgroundColor: selectedAttendance?.date === row.date ? "#F0FBF6" : "#FFFFFF",
          cursor: "pointer",
        }}
      >
        <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography sx={{ fontSize: 13, fontWeight: designWeights.heading, color: "#111A35" }}>
              {formatDate(row.date)}
            </Mui.Typography>
            <Mui.Typography sx={{ mt: 0.35, fontSize: 12, color: "#52607A" }}>
              {safeValue(row.shift)}
            </Mui.Typography>
          </Mui.Box>
          {statusHead ? (
            <Mui.Box sx={{ flexShrink: 0 }}>
              {renderAttendanceCell(row, statusHead)}
            </Mui.Box>
          ) : null}
        </Mui.Stack>
        <Mui.Box
          sx={{
            mt: 1.5,
            pt: 1.25,
            borderTop: "1px solid #E8EEF5",
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 1.25,
          }}
        >
          {detailHeads.map((head) => (
            <Mui.Box key={head.key} sx={{ minWidth: 0 }}>
              <Mui.Typography sx={{ fontSize: 10.5, color: "#52607A", fontWeight: designWeights.semibold }}>
                {head.name}
              </Mui.Typography>
              <Mui.Box sx={{ mt: 0.25, fontSize: 12.5, color: "#17203B", minWidth: 0 }}>
                {renderAttendanceCell(row, head)}
              </Mui.Box>
            </Mui.Box>
          ))}
        </Mui.Box>
      </Mui.Box>
    );
  };

  return (
    <Mui.Box sx={{ fontFamily: designFontFamily, width: "100%", minHeight: "100%", p: { xs: 1, md: 3 }, color: "#111A35" }}>
      <GlobalBreadcrumb
        sx={{ mb: 1.5 }}
        trail={[
          {
            label: "My Workspace",
            onClick: () => history.push("/admin/myzone/status"),
          },
          "My Attendance",
        ]}
      />
      <Mui.Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Mui.Box>
          <Mui.Typography sx={{ fontSize: 28, lineHeight: 1.15, fontWeight: designWeights.heading }}>
            My Attendance
          </Mui.Typography>
          <Mui.Typography sx={{ mt: 0.5, color: "#52607A", fontSize: 14, fontWeight: designWeights.regular }}>
            View today's attendance and your attendance history.
          </Mui.Typography>
        </Mui.Box>
      </Mui.Stack>

      <Mui.Grid container spacing={2}>
        <Mui.Grid item xs={12} lg={12}>
          <Mui.Stack spacing={2}>
            <Mui.Box
              sx={{
                ...cardSx,
                p: 2,
                borderColor: "#A6E6C2",
              }}
            >
              <Mui.Grid container spacing={2} alignItems="center">
                <Mui.Grid item xs={12} md={4}>
                  <Mui.Stack direction="row" spacing={2} alignItems="center">
                    <Mui.Avatar
                      src={userDetails?.image_url}
                      sx={{ width: 68, height: 68, bgcolor: "#DFF7EA" }}
                    />
                    <Mui.Box sx={{ minWidth: 0 }}>
                      <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Mui.Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#0F9F6E" }} />
                        <Mui.Typography sx={{ fontSize: 12, color: "#0F9F6E", fontWeight: designWeights.heading, letterSpacing: "0.02em" }}>
                          TODAY'S STATUS
                        </Mui.Typography>
                        <Mui.Chip
                          label="Live"
                          size="small"
                          sx={{
                            height: 22,
                            borderRadius: "6px",
                            bgcolor: "#DFF7EA",
                            color: "#0F9F6E",
                            fontSize: 11,
                            fontWeight: designWeights.semibold,
                          }}
                        />
                      </Mui.Stack>
                      <Mui.Typography sx={{ fontSize: 17, fontWeight: designWeights.heading, color: "#111A35" }}>
                        {userDetails?.first_name || userDetails?.username || userDetails?.user_name || "-"}
                      </Mui.Typography>
                      <Mui.Typography sx={{ fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
                        Employee ID: {safeValue(userDetails?.employee_id)}
                      </Mui.Typography>
                      <Mui.Typography sx={{ fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
                        Department: {safeValue(userDetails?.department_name)}
                      </Mui.Typography>
                      <Mui.Typography sx={{ fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
                        Shift: {safeValue(todayAttendance?.shift)}
                      </Mui.Typography>
                      <Mui.Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <LocationOnOutlined sx={{ fontSize: 14, color: "#52607A" }} />
                        <Mui.Typography sx={{ fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
                          {getLocation(todayAttendance || {})}
                        </Mui.Typography>
                      </Mui.Stack>
                    </Mui.Box>
                  </Mui.Stack>
                </Mui.Grid>

                <Mui.Grid item xs={12} md={8}>
                  <Mui.Grid container spacing={1.5}>
                    {[
                      {
                        label: "Check In",
                        value: getDisplayTime(todayAttendance?.check_in_time_formatted),
                        sub: getLocation(todayAttendance || {}),
                        color: "#0F9F6E",
                      },
                      {
                        label: "Check Out",
                        value: getDisplayTime(todayAttendance?.check_out_time_formatted),
                        sub: getLocation(todayAttendance || {}, "checkout"),
                        color: "#33415F",
                      },
                      {
                        label: "Worked Hours",
                        value: safeValue(todayAttendance?.working_hours, "00:00"),
                        sub: "",
                        color: "#0F9F6E",
                      },
                      {
                        label: "Break",
                        value: safeValue(todayAttendance?.break_hours, "00:00"),
                        sub: "",
                        color: "#F97316",
                      },
                      {
                        label: "Overtime",
                        value: safeValue(todayAttendance?.over_time, "00:00"),
                        sub: "",
                        color: "#6D4AFF",
                      },
                      {
                        label: "Status",
                        chip: todayAttendance ? <StatusChip row={todayAttendance} statusLabelMap={statusLabelMap} /> : null,
                        sub: "",
                      },
                    ].map((item) => (
                      <Mui.Grid item xs={12} sm={4} key={item.label}>
                        <Mui.Box
                          sx={{
                            px: 2,
                            py: 1,
                            borderLeft: "1px solid #DDE6EE",
                            minHeight: 68,
                          }}
                        >
                          <Mui.Typography sx={{ fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
                            {item.label}
                          </Mui.Typography>
                          {item.chip || (
                            <Mui.Typography sx={{ mt: 0.5, fontSize: 16, color: item.color, fontWeight: designWeights.heading }}>
                              {item.value}
                            </Mui.Typography>
                          )}
                          {item.sub ? (
                            <Mui.Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.35 }}>
                              <LocationOnOutlined sx={{ fontSize: 13, color: "#52607A" }} />
                              <Mui.Typography sx={{ fontSize: 11, color: "#52607A", fontWeight: designWeights.regular }}>
                                {item.sub}
                              </Mui.Typography>
                            </Mui.Stack>
                          ) : null}
                        </Mui.Box>
                      </Mui.Grid>
                    ))}
                  </Mui.Grid>
                </Mui.Grid>
              </Mui.Grid>
            </Mui.Box>

            <Mui.Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                gap: 2,
                alignItems: "stretch",
                width: "100%",
              }}
            >
              <Mui.Box sx={{ minWidth: 0 }}>
                <Mui.Box sx={{ ...cardSx, p: 2, height: "100%" }}>
                  <Mui.Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{ mb: 1.5 }}
                  >
                    <Mui.Stack direction="row" spacing={0.75} alignItems="center">
                      <Mui.Typography sx={{ fontSize: 15, fontWeight: designWeights.heading }}>
                        ATTENDANCE SUMMARY
                      </Mui.Typography>
                      <Mui.Typography sx={{ fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
                        (Selected Period)
                      </Mui.Typography>
                      <InfoOutlined sx={{ fontSize: 16, color: "#64748B" }} />
                    </Mui.Stack>
                    <Mui.Box sx={{ width: { xs: "100%", sm: 320 } }}>
                      <ApprovalDateRangeFilter
                        thisMonthToDate
                        startDate={startDate}
                        endDate={endDate}
                        dateRangeLabel={dateRangeLabel}
                        onChange={handleAttendanceDateRange}
                        includeCurrentDate={true}
                        showToday={true}
                        allowFutureDate={false}
                        sx={{
                          width: "100%",
                          minHeight: 40,
                          justifyContent: "space-between",
                        }}
                      />
                    </Mui.Box>
                  </Mui.Stack>

                  <Mui.Grid container spacing={1}>
                    {attendanceSummary.map((item) => (
                      <Mui.Grid item xs={6} sm={2.4} key={item.key}>
                        <MetricTile {...item} />
                      </Mui.Grid>
                    ))}
                  </Mui.Grid>

                  <Mui.Box sx={{ display: "flex", height: 14, mt: 2, borderRadius: "4px", overflow: "hidden" }}>
                    {attendanceSummary.map((item) => (
                      <Mui.Box
                        key={item.key}
                        sx={{
                          width: `${totalSummaryDays ? (numberValue(item.value) / totalSummaryDays) * 100 : 0}%`,
                          minWidth: numberValue(item.value) ? 8 : 0,
                          backgroundColor: item.color,
                        }}
                      />
                    ))}
                  </Mui.Box>
                  <Mui.Stack direction="row" justifyContent="space-between" sx={{ mt: 1.5 }}>
                    <Mui.Typography sx={{ fontSize: 13, color: "#17203B", fontWeight: designWeights.semibold }}>
                      Total: {totalSummaryDays || 0} Days
                    </Mui.Typography>
                    <Mui.Typography sx={{ fontSize: 13, color: "#0F9F6E", fontWeight: designWeights.heading }}>
                      {accountedPercent}% of total days
                    </Mui.Typography>
                  </Mui.Stack>
                  <Mui.Typography sx={{ mt: 1.25, fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
                    Day disposition shows how each calendar day in the selected period is accounted for.
                  </Mui.Typography>
                </Mui.Box>
              </Mui.Box>

              <Mui.Box sx={{ minWidth: 0 }}>
                <Mui.Box sx={{ ...cardSx, p: 2, height: "100%" }}>
                  <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
                    <Mui.Typography sx={{ fontSize: 15, fontWeight: designWeights.heading }}>
                      WORK METRICS
                    </Mui.Typography>
                    <Mui.Typography sx={{ fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
                      (Selected Period)
                    </Mui.Typography>
                    <InfoOutlined sx={{ fontSize: 16, color: "#64748B" }} />
                  </Mui.Stack>
                  <Mui.Grid container spacing={1}>
                    {workMetrics.map((item) => (
                      <Mui.Grid item xs={6} sm={4} key={item.key}>
                        <MetricTile {...item} compact />
                      </Mui.Grid>
                    ))}
                  </Mui.Grid>
                  <Mui.Typography sx={{ mt: 1.25, fontSize: 12, color: "#52607A", fontWeight: designWeights.regular }}>
                    These are event counts that annotate your worked days.
                  </Mui.Typography>
                </Mui.Box>
              </Mui.Box>
            </Mui.Box>

            <Mui.Box sx={{ ...cardSx, overflow: "hidden" }}>
              <CustomFilter
                title="Attendance History"
                titleOverride={titleOverride}
                filterHeaders={visibleHeaders}
                data={globalFilteredAttendanceRows}
                filteredData={workingHoursDetails}
                onFilteredDataChange={handleFilteredDataChange}
                screenAccess={true}
                showSaveButton={canPersistAttendancePreferences}
                hideCount={true}
                downloadFormats={["excel", "pdf", "csv"]}
                includeMetadata={true}
                metadata={reportMetadata}
                labeledToolbar={true}
                searchPlaceholder="Search attendance history..."
              />

              {handleLoading ? (
                <Loader info="Loading..." />
              ) : (
                <>
                  <Mui.Box sx={{ display: { xs: "grid", sm: "none" }, gap: 1.25, p: 1.5, pt: 0 }}>
                    {workingHoursDetails?.length === 0 ? (
                      <Mui.Box
                        sx={{
                          py: 4,
                          textAlign: "center",
                          color: "#52607A",
                          border: "1px dashed #E4EAF2",
                          borderRadius: "8px",
                        }}
                      >
                        No attendance records found.
                      </Mui.Box>
                    ) : (
                      paginatedRows.map((row) => (
                        <AttendanceMobileCard key={row.date} row={row} />
                      ))
                    )}
                  </Mui.Box>
                  <Mui.TableContainer ref={containerRef} sx={{ overflowX: "auto", display: { xs: "none", sm: "block" } }}>
                  <Mui.Table size="small" sx={{ minWidth: 920 }}>
                    <Mui.TableHead>
                      <Mui.TableRow sx={{ backgroundColor: "#F5F7FB" }}>
                        {visibleHeaders.map((header) => (
                          <Mui.TableCell
                            key={header.key}
                            sx={{
                              py: 1.15,
                              borderBottom: "1px solid #E4EAF2",
                              color: "#17203B",
                              fontSize: 12,
                              fontWeight: designWeights.heading,
                            }}
                          >
                            {header.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {workingHoursDetails?.length === 0 ? (
                        <TableEmptyState
                          dataLength={globalFilteredAttendanceRows}
                          colSpan={visibleHeaders.length}
                        />
                      ) : (
                        paginatedRows.map((user) => (
                          <Mui.TableRow
                            hover
                            key={user.date}
                            selected={selectedAttendance?.date === user.date}
                            onClick={() => selectAttendance(user)}
                            sx={{
                              cursor: "pointer",
                              "& .MuiTableCell-root": {
                                py: 1.1,
                                borderBottom: "1px solid #E8EEF5",
                                fontSize: 12,
                                fontWeight: designWeights.regular,
                                color: "#17203B",
                              },
                              "&.Mui-selected": {
                                backgroundColor: "#F0FBF6",
                              },
                            }}
                          >
                            {visibleHeaders.map((head) => (
                              <Mui.TableCell key={head.key}>
                                {renderAttendanceCell(user, head)}
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                        ))
                      )}
                    </Mui.TableBody>
                  </Mui.Table>
                  </Mui.TableContainer>
                </>
              )}

              <Mui.Box
                sx={{
                  px: 2,
                  py: 0.75,
                  borderTop: "1px solid #E4EAF2",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {paginationFooter({
                  page,
                  rowsPerPage,
                  total: workingHoursDetails?.length || 0,
                  itemLabel: "records",
                  onPageChange: setPage,
                  onRowsPerPageChange: handleChangeRowsPerPage,
                  rowsPerPageOptions,
                })}
              </Mui.Box>
            </Mui.Box>

            <Mui.Box
              sx={{
                ...cardSx,
                px: 2,
                py: 1.25,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 2,
              }}
            >
              <Mui.Stack direction="row" spacing={1} alignItems="center">
                <InfoOutlined sx={{ color: "#258BEA", fontSize: 20 }} />
                <Mui.Typography sx={{ fontSize: 13, color: "#33415F", fontWeight: designWeights.regular }}>
                  Tip: You can request correction for missing punch or shift using Attendance Correction.
                </Mui.Typography>
              </Mui.Stack>
              <Mui.Typography sx={{ fontSize: 13, color: "#0F9F6E", fontWeight: designWeights.semibold, whiteSpace: "nowrap" }}>
                Contact HR
              </Mui.Typography>
            </Mui.Box>
          </Mui.Stack>
        </Mui.Grid>

        <AttendanceDetailsPanel
          open={detailsOpen && Boolean(selectedAttendance)}
          attendance={selectedAttendance}
          statusLabelMap={statusLabelMap}
          onClose={() => setDetailsOpen(false)}
        />
      </Mui.Grid>
    </Mui.Box>
  );
};

export const IndividualWorkingHours = () => {
  const { domain, userDetails, userScreenAccess } = useSelector(
    (state) => state.tokenReducer,
  );
  const subModuleId = useMemo(
    () => resolveWorkingHoursSubModuleId(userScreenAccess),
    [userScreenAccess],
  );

  return (
    <ThemeProvider theme={designTheme}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Mui.CssBaseline />
        <IndividualWorkingHoursContent subModuleId={subModuleId} />
      </FilterProvider>
    </ThemeProvider>
  );
};

export default IndividualWorkingHours;
